import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

/**
 * What a screen shows when it has nothing to show.
 *
 * Every screen had been rolling its own — usually a grey sentence, sometimes nothing at all — so an
 * empty dashboard read as broken rather than as new. An empty state has a job: say what belongs
 * here, say why it is empty, and offer the one action that fixes it. All three, or it is just a
 * politer blank page.
 */
export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  illustration,
  compact = false,
}: {
  icon?: LucideIcon;
  title: string;
  /** Why it is empty and what to do — not a restatement of the title. */
  description: string;
  action?: ReactNode;
  /** Path under /illustrations. Decorative; the title and description carry the meaning. */
  illustration?: string;
  /** For an empty panel inside a populated screen, where a full-height block would be overbearing. */
  compact?: boolean;
}): JSX.Element => (
  <div
    className={`flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/20 text-center ${
      compact ? "px-4 py-6" : "px-6 py-12"
    }`}
  >
    {illustration && !compact ? (
      <img src={illustration} alt="" className="h-32 w-auto opacity-90" />
    ) : (
      Icon && (
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Icon aria-hidden className="h-5 w-5" />
        </span>
      )
    )}

    <div className="max-w-sm space-y-1">
      <p className="font-heading text-sm font-semibold">{title}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
    </div>

    {action}
  </div>
);
